import { formatValue, type Field, type QuerySpec } from "@yawhub/core";

export function dimensionLabel(
  value: unknown,
  dimension: QuerySpec["dimensions"][number] | undefined,
  fields: Field[],
  compact = false,
): string {
  const field = fields.find((f) => f.id === dimension?.fieldId);
  if (value == null || !dimension?.bucket) return formatValue(value, field);
  const day = String(value).slice(0, 10);
  const date = new Date(day + "T00:00:00Z");
  if (!Number.isFinite(date.getTime())) return formatValue(value, field);
  if (dimension.bucket === "month") {
    const label = new Intl.DateTimeFormat("da-DK", {
      month: compact ? "short" : "long",
      year: compact ? "2-digit" : "numeric",
      timeZone: "UTC",
    }).format(date);
    return label[0]!.toUpperCase() + label.slice(1);
  }
  if (dimension.bucket === "week") {
    // The Thursday determines the ISO week-year, including December/January.
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const year = date.getUTCFullYear();
    const week = Math.ceil(
      ((date.getTime() - Date.UTC(year, 0, 1)) / 86_400_000 + 1) / 7,
    );
    return compact
      ? `U${week} · ${String(year).slice(-2)}`
      : `Uge ${week} · ${year}`;
  }
  const label = formatValue(day, { ...field, type: "date" } as Field);
  return compact ? label.slice(0, 5) : label;
}

export function metricUnit(
  metric: { op: string; fieldId?: string } | undefined,
  fields: Field[],
): string {
  if (!metric || ["count_records", "count_values"].includes(metric.op))
    return "";
  return fields.find((f) => f.id === metric.fieldId)?.unit ?? "";
}

export function filterDescription(
  filter: QuerySpec["filters"][number],
  fields: Field[],
): string {
  const field = fields.find((f) => f.id === filter.fieldId),
    label = field?.label ?? "Felt";
  if (filter.op === "is_null") return `${label} er tom`;
  if (filter.op === "is_not_null") return `${label} er udfyldt`;
  const date = field && ["date", "datetime"].includes(field.type);
  const operators = {
    eq: "er",
    ne: "er ikke",
    gt: date ? "er efter" : "er over",
    gte: date ? "er fra" : "er mindst",
    lt: date ? "er før" : "er under",
    lte: date ? "er til og med" : "er højst",
    contains: "indeholder",
    in: "er en af",
  };
  const unit =
    field && ["integer", "decimal"].includes(field.type) && field.unit
      ? " " + field.unit
      : "";
  const formatted = (value: string | boolean) =>
    `${field ? formatValue(value, field) : String(value)}${unit}`;
  const value = Array.isArray(filter.value)
    ? filter.value.map(formatted).join(" eller ")
    : filter.value === undefined
      ? ""
      : formatted(filter.value);
  return `${label} ${operators[filter.op]} ${value}`.trim();
}
